<template>
  <div
    class="linkZipUpload"
    :class="{
      'linkZipUpload--disabled': disabled,
      'linkZipUpload--error': phase === 'error',
      'linkZipUpload--dragover': dragActive,
    }"
    data-field-key="jsonConfig.url"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div v-if="dragActive" class="linkZipUpload__dropHint" aria-hidden="true">
      <i class="el-icon-upload2 linkZipUpload__triggerIcon" />
      <span>{{ hasUrl ? '松开即替换当前文件' : '松开即上传' }}</span>
    </div>
    <!-- 默认态 -->
    <button
      v-if="phase === 'idle' && !hasUrl"
      type="button"
      class="linkZipUpload__trigger"
      :disabled="disabled"
      @click="pickFile"
    >
      <i class="el-icon-upload2 linkZipUpload__triggerIcon" aria-hidden="true" />
      <span class="linkZipUpload__triggerLabel">{{ triggerLabel }}</span>
      <span class="linkZipUpload__triggerHint">{{ triggerHint }}</span>
    </button>

    <!-- 上传中 -->
    <div v-else-if="phase === 'uploading'" class="linkZipUpload__panel">
      <div class="linkZipUpload__fileRow">
        <i class="el-icon-document linkZipUpload__fileIcon" aria-hidden="true" />
        <span class="linkZipUpload__fileName">{{ activeFileName }}</span>
      </div>
      <el-progress
        :percentage="uploadProgress"
        :stroke-width="8"
        :show-text="true"
        class="linkZipUpload__progress"
      />
      <button type="button" class="linkZipUpload__btn" @click="cancelUpload">
        取消上传
      </button>
    </div>

    <!-- 解析中 -->
    <div v-else-if="phase === 'parsing'" class="linkZipUpload__panel linkZipUpload__panel--parsing">
      <i class="el-icon-loading linkZipUpload__spin" aria-hidden="true" />
      <span>正在检查资源包结构…</span>
    </div>

    <!-- 成功：居中紧凑块，避免大白框里左上角三行文字显得空 -->
    <div v-else-if="phase === 'success' || (phase === 'idle' && hasUrl)" class="linkZipUpload__panel linkZipUpload__panel--success">
      <div class="linkZipUpload__successBody">
        <div class="linkZipUpload__successBadge" aria-hidden="true">
          <i class="el-icon-check" />
        </div>
        <div class="linkZipUpload__successTitle">上传成功</div>
        <p v-if="displayFileSize" class="linkZipUpload__successHint">{{ displayFileSize }}</p>
        <ul v-if="checklist.length" class="linkZipUpload__checklist">
          <li
            v-for="item in checklist"
            :key="item.label"
            class="linkZipUpload__checkItem"
          >
            <span>{{ item.label }}</span>
            <span class="linkZipUpload__checkMark" aria-hidden="true">✓</span>
          </li>
        </ul>
        <div class="linkZipUpload__actions">
          <button type="button" class="linkZipUpload__btn" @click="pickFile">
            重新上传
          </button>
          <button type="button" class="linkZipUpload__btn linkZipUpload__btn--danger" @click="clearFile">
            删除
          </button>
        </div>
      </div>
    </div>

    <!-- 失败 -->
    <div v-else-if="phase === 'error'" class="linkZipUpload__panel linkZipUpload__panel--error">
      <div class="linkZipUpload__errorHead">
        <i class="el-icon-warning linkZipUpload__errorIcon" aria-hidden="true" />
        <span>ZIP 包结构不完整</span>
      </div>
      <p v-if="missingItems.length" class="linkZipUpload__missingTitle">缺少：</p>
      <ul class="linkZipUpload__missingList">
        <li v-for="item in missingItems" :key="item">- {{ item }}</li>
      </ul>
      <button type="button" class="linkZipUpload__btn" @click="pickFile">
        重新上传
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { releasePending, uploadAssetFile } from '../../../../utils/directCloudUpload'
import {
  isZipOnlyType,
  pickDroppedFile,
  validateLocalFileForAssetType,
  validateUploadedUrlForAssetType,
} from '../genericResourceUploadRules'
import {
  formatFileSize,
  inspectZipStructure,
} from './genericResourceZipStructureCheck'

export default {
  name: 'GenericResourceZipUpload',
  props: {
    assetType: { type: String, default: '' },
    url: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      phase: 'idle',
      uploadProgress: 0,
      activeFileName: '',
      activeFileSize: '',
      checklist: [],
      missingItems: [],
      uploadCancelSource: null,
      // 拖过子元素时 dragenter/dragleave 成对触发，用计数判断指针是否还在组件内，避免高亮闪烁。
      dragDepth: 0,
    }
  },
  computed: {
    hasUrl() {
      return !!String(this.url || '').trim()
    },
    /** 检查或上传进行中时不接受新文件，其余状态拖入等同于「上传」或「重新上传」。 */
    canDrop() {
      return !this.disabled && this.phase !== 'parsing' && this.phase !== 'uploading'
    },
    dragActive() {
      return this.dragDepth > 0 && this.canDrop
    },
    isZipType() {
      return isZipOnlyType(this.assetType)
    },
    triggerLabel() {
      return this.isZipType ? '上传 ZIP' : '上传资源文件'
    },
    triggerHint() {
      if (this.disabled) {
        return '请先在顶栏选择资源类型'
      }
      return this.isZipType ? '拖到这里或点击选择，仅支持 .zip 格式' : '拖到这里或点击选择本地文件'
    },
    displayFileSize() {
      return this.activeFileSize
    },
  },
  watch: {
    assetType() {
      this.resetTransientState()
    },
    url(next, prev) {
      if (!next && prev) {
        this.resetTransientState()
      } else if (next && this.phase === 'idle') {
        this.phase = 'success'
      }
    },
  },
  mounted() {
    if (this.hasUrl) {
      this.phase = 'success'
    }
  },
  beforeDestroy() {
    this.abortUpload()
  },
  methods: {
    extractNameFromUrl(url) {
      if (!url) {
        return ''
      }
      try {
        const path = String(url).split('?')[0]
        const parts = path.split('/')
        return parts[parts.length - 1] || url
      } catch (e) {
        return url
      }
    },
    resetTransientState() {
      this.abortUpload()
      this.phase = this.hasUrl ? 'success' : 'idle'
      this.uploadProgress = 0
      this.activeFileName = this.hasUrl ? this.extractNameFromUrl(this.url) : ''
      this.activeFileSize = ''
      this.checklist = []
      this.missingItems = []
    },
    pickFile() {
      if (this.disabled) {
        this.$message.info('请先在顶栏选择资源类型')
        return
      }
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      if (this.isZipType) {
        input.setAttribute('accept', '.zip,application/zip')
      }
      input.addEventListener('change', (event) => {
        const file = event.target.files && event.target.files[0]
        input.remove()
        if (file) {
          this.startUpload(file)
        }
      })
      input.click()
    },
    isFileDrag(event) {
      const types = event.dataTransfer && event.dataTransfer.types
      return !!types && Array.from(types).includes('Files')
    },
    onDragEnter(event) {
      if (this.isFileDrag(event)) {
        this.dragDepth += 1
      }
    },
    onDragOver(event) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = this.canDrop ? 'copy' : 'none'
      }
    },
    onDragLeave(event) {
      if (!this.isFileDrag(event) || this.dragDepth === 0) {
        return
      }
      // 按 Esc 取消或直接拖出窗口时 relatedTarget 为空，计数可能对不齐，离开组件就清零。
      const target = event.relatedTarget
      this.dragDepth = target && this.$el.contains(target) ? this.dragDepth - 1 : 0
    },
    onDrop(event) {
      this.dragDepth = 0
      if (!this.canDrop) {
        if (this.phase === 'parsing' || this.phase === 'uploading') {
          this.$message.info('正在处理上一个文件，请稍候')
        }
        return
      }
      const picked = pickDroppedFile(event.dataTransfer && event.dataTransfer.files)
      if (!picked.ok) {
        if (picked.message) {
          this.$message.warning(picked.message)
        }
        return
      }
      this.startUpload(picked.file)
    },
    abortUpload() {
      if (this.uploadCancelSource) {
        this.uploadCancelSource.cancel('upload canceled')
        this.uploadCancelSource = null
      }
    },
    cancelUpload() {
      this.abortUpload()
      this.phase = this.hasUrl ? 'success' : 'idle'
      this.uploadProgress = 0
    },
    clearFile() {
      this.resetTransientState()
      this.phase = 'idle'
      this.$emit('update:url', '')
      this.$emit('cleared')
    },
    async startUpload(file) {
      const formatCheck = validateLocalFileForAssetType(this.assetType, file.name, file.size)
      if (!formatCheck.ok) {
        this.$message.error(formatCheck.message)
        return
      }

      this.activeFileName = file.name
      this.activeFileSize = formatFileSize(file.size)
      this.checklist = []
      this.missingItems = []

      // zip-only：先本地结构检查，避免不合规包先打上 CDN
      if (this.isZipType) {
        this.phase = 'parsing'
        const result = await inspectZipStructure(file, this.assetType)
        if (!result.ok) {
          this.phase = 'error'
          this.missingItems = result.missing
          return
        }
        this.checklist = result.items.filter((item) => item.ok)
      }

      this.phase = 'uploading'
      this.uploadProgress = 0

      try {
        const previousUrl = this.url
        const uploadedUrl = await this.uploadWithProgress(file)
        if (previousUrl && previousUrl !== uploadedUrl) {
          releasePending(previousUrl)
        }
        const urlCheck = validateUploadedUrlForAssetType(this.assetType, uploadedUrl)
        if (!urlCheck.ok) {
          this.phase = 'error'
          this.missingItems = [urlCheck.message]
          return
        }

        this.phase = 'success'
        this.$emit('update:url', uploadedUrl)
        this.$emit('uploaded', uploadedUrl, file.name)
      } catch (err) {
        if (axios.isCancel(err)) {
          this.phase = this.hasUrl ? 'success' : 'idle'
          return
        }
        this.phase = 'error'
        this.missingItems = ['文件上传失败，请检查网络后重试']
      }
    },
    uploadWithProgress(file) {
      // axios 0.21 仅支持 CancelToken，不支持 AbortController.signal
      this.uploadCancelSource = axios.CancelToken.source()
      const cancelSource = this.uploadCancelSource
      return uploadAssetFile(file, {
        trackForDiscard: true,
        cancelToken: cancelSource.token,
        onProgress: (event) => {
          if (!event.total) {
            return
          }
          this.uploadProgress = Math.min(99, Math.round((event.loaded / event.total) * 100))
        },
      })
        .then((data) => {
          this.uploadProgress = 100
          if (!data || data.errCode === '500') {
            return Promise.reject(new Error((data && data.errMessage) || '上传失败'))
          }
          return data.data
        })
        .finally(() => {
          if (this.uploadCancelSource === cancelSource) {
            this.uploadCancelSource = null
          }
        })
    },
  },
}
</script>

<style scoped>
.linkZipUpload {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
}

.linkZipUpload--dragover .linkZipUpload__trigger,
.linkZipUpload--dragover .linkZipUpload__panel {
  border-color: var(--link-accent, #a67c52);
  background: var(--link-accent-soft, #faf3eb);
}

/* 覆盖层不接收指针事件，拖拽事件仍落在组件根上，计数才能正确归零。 */
.linkZipUpload__dropHint {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px dashed var(--link-accent, #a67c52);
  border-radius: 12px;
  background: rgba(250, 243, 235, 0.92);
  font-size: var(--link-text-sm, 14px);
  font-weight: 600;
  color: var(--link-ink, #2c2825);
  pointer-events: none;
}

.linkZipUpload--disabled {
  opacity: 0.62;
  pointer-events: none;
}

.linkZipUpload__trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  min-height: 200px;
  padding: 16px;
  box-sizing: border-box;
  border: 1px dashed var(--link-line-strong, #e5dfd6);
  border-radius: 12px;
  background: var(--link-surface-muted, #f7f4ef);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.linkZipUpload__trigger:hover:not(:disabled) {
  border-color: var(--link-accent, #a67c52);
  background: var(--link-accent-soft, #faf3eb);
}

.linkZipUpload__triggerIcon {
  font-size: var(--link-icon-xl, 32px);
  color: var(--link-accent, #a67c52);
  line-height: 1;
}

.linkZipUpload__triggerLabel {
  font-size: var(--link-text-sm, 14px);
  font-weight: 600;
  color: var(--link-ink, #2c2825);
}

.linkZipUpload__triggerHint {
  font-size: var(--link-text-xs, 12px);
  color: var(--link-ink-muted, #78716c);
  text-align: center;
  line-height: 1.45;
}

.linkZipUpload__panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  min-height: 200px;
  padding: 16px;
  box-sizing: border-box;
  border: 1px solid var(--link-line-strong, #e7e5e4);
  border-radius: 12px;
  background: var(--link-card-surface, #fff);
}

.linkZipUpload__panel--success {
  align-items: center;
  justify-content: center;
  border-color: rgba(22, 163, 74, 0.22);
  background:
    radial-gradient(circle at 50% 28%, rgba(22, 163, 74, 0.1), transparent 55%),
    var(--link-surface-muted, #f7f4ef);
}

.linkZipUpload__successBody {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 220px;
  text-align: center;
}

.linkZipUpload__successBadge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: rgba(22, 163, 74, 0.14);
  color: #15803d;
  font-size: 20px;
  line-height: 1;
}

.linkZipUpload__successTitle {
  font-size: 14px;
  font-weight: 600;
  color: var(--link-ink, #2c2825);
  letter-spacing: 0.02em;
}

.linkZipUpload__successHint {
  margin: -4px 0 0;
  font-size: 12px;
  color: var(--link-ink-muted, #78716c);
}

.linkZipUpload__panel--parsing {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: var(--link-ink-secondary, #57534e);
}

.linkZipUpload__spin {
  font-size: 18px;
  color: var(--link-accent, #a67c52);
}

.linkZipUpload__fileRow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.linkZipUpload__fileIcon {
  font-size: 18px;
  color: var(--link-ink-muted, #78716c);
}

.linkZipUpload__fileName {
  font-size: 13px;
  font-weight: 500;
  color: var(--link-ink, #2c2825);
  word-break: break-all;
}

.linkZipUpload__progress {
  margin-bottom: 8px;
}

.linkZipUpload__checklist {
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  list-style: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
  text-align: left;
}

.linkZipUpload__checkItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
  font-size: 12px;
  color: var(--link-ink-secondary, #57534e);
}

.linkZipUpload__checkMark {
  color: #16a34a;
  font-weight: 600;
}

.linkZipUpload__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 2px;
}

.linkZipUpload__btn {
  padding: 5px 12px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  color: var(--link-ink-secondary, #57534e);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.linkZipUpload__btn:hover {
  border-color: var(--link-accent, #a67c52);
  color: var(--link-ink, #2c2825);
  background: #fff;
}

.linkZipUpload__btn--danger {
  color: #b91c1c;
  border-color: rgba(185, 28, 28, 0.28);
}

.linkZipUpload__btn--danger:hover {
  border-color: rgba(185, 28, 28, 0.45);
  color: #991b1b;
  background: rgba(254, 242, 242, 0.9);
}

.linkZipUpload__panel--error {
  border-color: rgba(220, 38, 38, 0.35);
  background: rgba(254, 242, 242, 0.45);
}

.linkZipUpload__errorHead {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #b91c1c;
}

.linkZipUpload__errorIcon {
  font-size: 16px;
}

.linkZipUpload__missingTitle {
  margin: 0 0 4px;
  font-size: 12px;
  color: #991b1b;
}

.linkZipUpload__missingList {
  margin: 0 0 10px;
  padding-left: 16px;
  font-size: 12px;
  color: #991b1b;
}
</style>
